import { useCallback, useState, useRef } from 'react'

/**
 * This hook is used to toggle a boolean state after a given duration.
 * The state will revert to the initial state after the given duration.
 *
 * Useful when showing ephemeral messages that timeout after a given duration.
 *
 * @param duration - The duration in milliseconds after which the state will revert to the initial state.
 * @param initial - The initial state.
 * @returns A tuple containing the current state and a function to toggle the state.
 */
export const useTimedState = (duration = 3000, initial = false): [boolean, () => void] => {
    const [state, setState] = useState(initial)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const trigger = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)

        const toggledState = !initial
        setState(toggledState)

        timeoutRef.current = setTimeout(() => {
            setState(initial)
            timeoutRef.current = null
        }, duration)
    }, [duration, initial])

    return [state, trigger]
}
